import {
  ApiKeyFragment,
  BdoState,
  EmployeeContractFragment,
  EmployeeFragment,
  LoanEmployeeFragment,
  PageOnPosTaskFragment,
  PosApplicationTaskBriefFragment,
  TaskStates,
  UserContractsFragment,
} from '@/app/graphql/access-control/sdk.access-control';
import { date } from 'quasar';
import formatDate = date.formatDate;
import { ApiKey, TaskWithStatus } from '@/shared/services/access-control/types';
import { ORGANIZATION } from '@/shared/web/type';
import { nativeApp } from '@/shared/services/native-app';

export function mapEmployeeContract(employees: EmployeeContractFragment[]) {
  return employees.map((employee) => {
    return {
      ...employee,
      createdDate: formatDate(employee.createdDate, 'DD.MM.YYYY'),
    };
  });
}

function mapSelectedContract(contracts: UserContractsFragment[]) {
  const selectedOrganization = localStorage.getItem(ORGANIZATION.ORG_ID_KEY);
  if (!selectedOrganization) return undefined;
  return contracts.find((item) => item.orgReferenceId === +selectedOrganization);
}

export function mapEmployeeSecure(employee: EmployeeFragment) {
  const contract = mapSelectedContract(employee.userContracts);
  const currentContract = contract ? contract : employee.userContracts[0];
  localStorage.setItem(ORGANIZATION.ORG_ID_KEY, currentContract.orgReferenceId.toString());
  nativeApp.setOrgReferenceId(currentContract.orgReferenceId.toString());

  return {
    ...employee,
    userContracts: employee.userContracts.sort((first, second) => (first.createdDate < second.createdDate ? 1 : -1)),
    authorized: true,
    currentContract,
  };
}

const mapStatusSeller = (status: TaskStates) => {
  switch (status) {
    case TaskStates.Approved:
      return {
        label: 'INSTALLMENT.STATUS.APPROVED',
        classList: 'status-success',
      };
    case TaskStates.OnVerification:
      return {
        label: 'INSTALLMENT.STATUS.ON_VERIFICATION',
        classList: 'status-warning',
      };
    case TaskStates.Declined:
      return {
        label: 'INSTALLMENT.STATUS.DECLINED',
        classList: 'status-danger',
      };
    default:
      return {
        label: 'INSTALLMENT.STATUS.DECLINED',
        classList: 'status-danger',
      };
  }
};
export async function mapSellerInstallment(seller: LoanEmployeeFragment) {
  return {
    totalPage: seller.totalPages,
    list: seller.list.map((item) => ({
      ...item,
      locationId: +item.locationId,
      pointName: null,
      taskState: {
        ...mapStatusSeller(item.taskState),
        status: item.taskState,
      },
    })),
  };
}

export function mapPosApplicationStatus(status: BdoState | null) {
  switch (status) {
    case BdoState.Completed:
      return {
        label: 'POS.STATUS.COMPLETED',
        classList: 'status-success',
      };
    case BdoState.InProcess:
      return {
        label: 'POS.STATUS.IN_PROCESS',
        classList: 'status-warning',
      };
    case BdoState.Rejected:
      return {
        label: 'POS.STATUS.REJECTED',
        classList: 'status-danger',
      };
    default:
      return {
        label: 'POS.STATUS.IN_PROCESS',
        classList: 'status-warning',
      };
  }
}

export function mapPosApplicationTask<T extends PosApplicationTaskBriefFragment>(task: T): TaskWithStatus<T> {
  return {
    ...task,
    bdoStatus: mapPosApplicationStatus(task.newValue.bdoState),
    createdDate: formatDate(task.createdDate, 'DD.MM.YYYY HH:mm'),
  };
}

export function mapPosApplicationTasks(taskPageable: PageOnPosTaskFragment | null) {
  if (!taskPageable) return null;

  return {
    ...taskPageable,
    list: taskPageable.list.map((item) => mapPosApplicationTask(item)),
  };
}

export function mapApiKey(apiKey: ApiKeyFragment): ApiKey {
  const isActive = Date.now() <= new Date(apiKey.validUntil).getTime();
  return {
    ...apiKey,
    isActive,
    createdDate: formatDate(apiKey.createdDate, 'DD.MM.YYYY HH:mm'),
    validUntil: formatDate(apiKey.validUntil, 'DD.MM.YYYY HH:mm'),
  };
}
